/*
 * Eigenschaften eines Teilschemas: welches Teilschema, Kopf ja/nein, und je
 * Wertfeld ein Datenpunkt. Dazu "Automatisch zuordnen" ueber die
 * Climatix-Adresse oder den Namen am Regler (hmi/parts/s400/autoBind.ts).
 */

import { useState } from 'preact/hooks';
import type { SchematicPartWidget } from '../../../widgets';
import { BindingField } from '../BindingField';
import { CheckboxField, Field, Section, patcher, type WidgetEditorProps } from '../fields';
import { usePlantDatapoints } from '../../../../components/DatapointPicker';
import { formatBindingKey } from '../../../bindingKey';
import { S400_FAMILIES, S400_PARTS, s400Part, s400PartKey } from '../../../parts/s400/index';
import { autoBindFields } from '../../../parts/s400/autoBind';

const TYPE_LABEL = { zahl: 'Zahl', schalter: 'Ein/Aus', text: 'Text' } as const;

export function SchematicPartEditor({ widget, plantId, onChange }: WidgetEditorProps<SchematicPartWidget>) {
  const patch = patcher(widget, onChange);
  const part = s400Part(widget.part);
  const points = usePlantDatapoints(plantId);
  const [note, setNote] = useState<string | null>(null);

  function setBinding(fieldId: string, key: string | undefined): void {
    const next = { ...widget.bindings };
    if (key) next[fieldId] = key; else delete next[fieldId];
    patch({ bindings: next });
  }

  function autoBind(): void {
    if (!part || points.status !== 'ok') return;
    const leaves = points.data.rows.map((r) => ({
      key: formatBindingKey(plantId, r.leaf.id),
      symbol: r.leaf.symbol,
      displayName: r.leaf.displayName,
      attributes: r.leaf.attributes,
    }));
    const found = autoBindFields(part.fields, leaves, widget.bindings);
    const n = Object.keys(found).length;
    const open = part.fields.filter((f) => !widget.bindings[f.id] && !found[f.id]).length;
    setNote(n === 0
      ? `Nichts eindeutig gefunden. ${open} Felder bleiben frei.`
      : `${n} zugeordnet${open ? `, ${open} bleiben frei` : ''}.`);
    if (n > 0) patch({ bindings: { ...widget.bindings, ...found } });
  }

  const bound = part ? part.fields.filter((f) => widget.bindings[f.id]).length : 0;

  return (
    <>
      <Section title="Teilschema" hint="Siemens-Climatix-S400-Anwendung. Das Kurzzeichen steht im Kopf der Karte.">
        <Field label="Teilschema">
          <select
            class="hv-pf__input"
            value={widget.part}
            onChange={(e) => patch({ part: (e.currentTarget as HTMLSelectElement).value })}
          >
            {!part && <option value={widget.part}>{widget.part} (unbekannt)</option>}
            {S400_FAMILIES.map((fam) => (
              <optgroup key={fam.key} label={fam.label}>
                {S400_PARTS.filter((p) => p.family === fam.key).map((p) => (
                  <option key={p.code} value={s400PartKey(p.code)}>
                    {p.code} {p.title}{p.compact ? ' (kompakt)' : ''}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
        </Field>
        <CheckboxField
          label="Kopf mit Titel und Kurzzeichen"
          checked={widget.showHeader !== false}
          onChange={(v) => patch({ showHeader: v ? undefined : false })}
        />
      </Section>

      {part && (
        <Section
          title={`Datenpunkte (${bound} von ${part.fields.length})`}
          hint="Je Feld ein Punkt der Anlage. Unter dem Feld steht der Name am Regler; Pumpen in der Zeichnung laufen mit ihrem Ein/Aus-Feld."
        >
          <div class="hv-pf__actions">
            <button
              type="button"
              class="hv-btn"
              disabled={points.status !== 'ok'}
              title="Über die Climatix-Adresse oder den Namen am Regler, nur eindeutige Treffer, gebundene Felder bleiben"
              onClick={autoBind}
            >
              Automatisch zuordnen
            </button>
            {note && <span class="hv-pf__note" role="status">{note}</span>}
          </div>
          {part.fields.map((f) => (
            <div class="hv-pf__binding" key={f.id}>
              <BindingField
                label={`${f.label.replace(/­/g, '')} (${TYPE_LABEL[f.type]}${f.unit ? `, ${f.unit}` : ''})`}
                value={widget.bindings[f.id]}
                plantId={plantId}
                filter={f.type === 'schalter' ? undefined : f.type === 'zahl' ? { valueType: 'number' } : undefined}
                hint={[f.name, f.id].filter(Boolean).join(' · ')}
                onChange={(next) => setBinding(f.id, next)}
              />
            </div>
          ))}
        </Section>
      )}
    </>
  );
}
